import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { EventPipelineService } from '@bootstrap/services/event-pipeline.service';
import { LoggerService } from '@core/services/logger/logger.service';
import { EventName, ImplantsViewDataChanged } from '@itero/itero-client-communication';
import { TlkButtonComponent } from '@itero/ui-toolkit-angular/button';
import { TlkExpansionAreaComponent, TlkExpansionContentDirective } from '@itero/ui-toolkit-angular/expansion-area';
import { TLK_MODAL_WINDOW_DATA, TlkModalWindowModule, TlkModalWindowRef } from '@itero/ui-toolkit-angular/modal-window';
import { RxIntuitiveAdditionalInfoModalService } from '@modules/rx-intuitive-crown-additional-info/components/rx-intuitive-additional-info-modal/rx-intuitive-additional-info-modal.service';
import {
	AdditionalInfoFieldKey,
	AdditionalInfoIds,
	AdditionalInfoResult
} from '@modules/rx-intuitive-crown-additional-info/models/additional-info';
import { RxIntuitiveCrownAdditionalInfoRulesService } from '@modules/rx-intuitive-crown-additional-info/services/rx-intuitive-crown-additional-info-rules.service';
import { getDisabledFieldsClearPayload } from '@modules/rx-intuitive-crown-additional-info/utils/get-disabled-fields-clear-payload';
import { RxIntuitiveCrownComponent } from '@modules/rx-intuitive-crown/component/rx-intuitive-crown.component';
import { CrownForm } from '@modules/rx-intuitive-crown/models/crown-form';
import { CrownSelectionContext } from '@modules/rx-intuitive-crown/models/crown-models';
import { IntuitiveIndication } from '@modules/rx-intuitive-teeth-orchestrator/models';
import { TOOTH_MODAL_ACTION } from '@modules/rx-intuitive-teeth-orchestrator/models/tooth-modal';
import { RxIntuitiveIndicationMapperService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-indication-mapper.service';
import { getNewTooth, Tooth } from '@modules/teeth-diagram/models/tooth';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { ImplantService } from '@modules/tooth-editor/services/implant.service';
import { RestorationTypeIds } from '@modules/tooth-editor/restoration-type.facade';
import { ToothEditorSharedModule } from '@modules/tooth-editor/tooth-editor-shared.module';
import { TranslateModule } from '@ngx-translate/core';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { RxBannerComponent } from '@shared/components/rx-banner/rx-banner.component';
import { ImplantsViewComponent } from '@shared/implants-view/implants-view.component';
import { ImplantsViewService } from '@shared/implants-view/implants-view.service';
import { ImplantsViewAppParameter } from '@shared/implants-view/models/implants-view-app-parameter';
import { IdName } from '@shared/models/id-name';
import { Implant } from '@shared/models/implant';
import { RoleTypeEnum } from '@shared/models/role-type';
import { MaterialOption } from '@shared/models/material-option';
import { getSortedListByName } from '@shared/services/sort-list-by-name-util';
import { ValidateRxService } from '@shared/services/validate-rx';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';
import { IndicationTranslatePipe } from '@shared/pipes/indication-translate.pipe';
import { EMPTY_INTUITIVE_IMPLANTS_FORM_VALUE, IntuitiveImplantsFormValue } from '../rx-intuitive-implants-form/implants-form.model';
import { RxIntuitiveImplantsFormComponent } from '../rx-intuitive-implants-form/rx-intuitive-implants-form.component';
import { RxIntuitiveIndicationBadgeComponent } from '../rx-intuitive-indication-badge/rx-intuitive-indication-badge.component';
import type {
	RxIntuitiveImplantsViewModalData,
	RxIntuitiveImplantsViewModalResult,
	ToothForImplantsViewModal
} from './rx-intuitive-implants-view-modal.model';

@Component({
	selector: 'rx-intuitive-implants-view-modal',
	templateUrl: './rx-intuitive-implants-view-modal.component.html',
	styleUrls: ['./rx-intuitive-implants-view-modal.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		TranslateModule,
		TlkButtonComponent,
		TlkExpansionAreaComponent,
		TlkExpansionContentDirective,
		TlkModalWindowModule,
		RxBannerComponent,
		RxIntuitiveCrownComponent,
		RxIntuitiveIndicationBadgeComponent,
		IndicationTranslatePipe,
		ToothEditorSharedModule,
		ImplantsViewComponent,
		RxIntuitiveImplantsFormComponent
	]
})
export class RxIntuitiveImplantsViewModalComponent extends BaseDestroyableComponent implements OnInit {
	/** TLK expansion sections (replaces `mat-accordion` / `mat-expansion-panel` with independent areas). */
	restorationTypeSectionExpanded = false;

	readonly crownSectionExpanded$ = new BehaviorSubject<boolean>(false);


	readonly implantsViewAppParameters$: Observable<ImplantsViewAppParameter>;

	readonly shouldShowNewImplantsView$ = this.implantsViewService.shouldShowNewImplantsView$;

	readonly teeth: readonly ToothForImplantsViewModal[];

	private readonly teethIds: readonly number[];

	private latestImplantsViewEvent: ImplantsViewDataChanged | null = null;

	readonly crownFormData$ = new BehaviorSubject<CrownForm>({
		teethIds: [],
		material: null,
		shadeSystem: null,
		shadeBody: null,
		specification: null
	});

	readonly restorationTypeData$ = new BehaviorSubject<RestorationTypeIds>({
		ImplantBasedRestorationTypeId: null,
		AbutmentType: null,
		AbutmentMaterialId: null
	});

	readonly additionalInfoData$ = new BehaviorSubject<AdditionalInfoIds>({});

	private readonly pendingAdditionalInfoClears: { toothIds: number[]; clearedFields: AdditionalInfoFieldKey[] }[] = [];

	private crownContext: CrownSelectionContext | null = null;

	readonly hasAdditionalInfoButtonSaved$: Observable<boolean>;

	readonly isReadOnly$: Observable<boolean>;

	readonly showOfflineBanner$: Observable<boolean>;

	/** Shows "Required information missing" on the collapsed Crown header, gated the same way as other required-field messages (on send). */
	readonly showCrownRequiredInfoMessage$: Observable<boolean>;

	readonly showCrownRequiredInfoBadge$: Observable<boolean>;

	legacyImplantsFormValue: IntuitiveImplantsFormValue = EMPTY_INTUITIVE_IMPLANTS_FORM_VALUE;

	implantManufacturerOptions: IdName[] = [];

	implantTypeScanBodyOptions: Implant[] = [];

	readonly scanBodyManufacturerOptions$: Observable<IdName[]> = this.implantService.scanBodyManufacturerOptions$.pipe(
		map(scanBodyManufacturers => getSortedListByName(this.implantService.filterActiveScanBodyManufacturers({ scanBodyManufacturers })))
	);

	readonly isFavoritesHidden$: Observable<boolean> = this.shellQuery.userRole$.pipe(
		map(userRole => userRole !== RoleTypeEnum.Doctor || !this.shellQuery.isProcedureFlow),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	get isProcedureFlow(): boolean {
		return this.shellQuery.isProcedureFlow;
	}

	constructor(
		private readonly dialogRef: TlkModalWindowRef<RxIntuitiveImplantsViewModalResult, RxIntuitiveImplantsViewModalComponent>,
		@Inject(TLK_MODAL_WINDOW_DATA) readonly modalData: RxIntuitiveImplantsViewModalData,
		private readonly eventPipelineService: EventPipelineService,
		private readonly teethDiagramQuery: TeethDiagramQuery,
		private readonly indicationMapper: RxIntuitiveIndicationMapperService,
		private readonly additionalInfoRules: RxIntuitiveCrownAdditionalInfoRulesService,
		private readonly additionalInfoModalService: RxIntuitiveAdditionalInfoModalService,
		private readonly logger: LoggerService,
		private readonly implantsViewService: ImplantsViewService,
		private readonly implantService: ImplantService,
		private readonly shellQuery: ShellQuery,
		private readonly validateRxService: ValidateRxService,
		private readonly changeDetectorRef: ChangeDetectorRef
	) {
		super();

		this.teeth = modalData.teeth;
		this.teethIds = modalData.teeth.map(t => t.toothId);
		this.implantsViewAppParameters$ = modalData.implantsViewAppParameters$;

		const primaryTooth = this.teeth[0];

		this.crownFormData$.next(this.buildCrownForm(modalData.teeth));
		this.restorationTypeData$.next({ ...primaryTooth?.restorationTypeIds });
		this.additionalInfoData$.next({ ...primaryTooth?.additionalInfoIds });

		// TODO: replace teethDiagramQuery usage with something else
		this.hasAdditionalInfoButtonSaved$ = combineLatest([
			this.teethDiagramQuery.teethFlat$,
			this.restorationTypeData$,
			this.additionalInfoData$
		]).pipe(
			map(([teethFlat, restoration, additionalInfo]) => {
				const sessionIdSet = new Set(this.teethIds);

				const overlaid = teethFlat.map(t =>
					t.ToothID != null && sessionIdSet.has(t.ToothID) ? ({ ...t, ...restoration, ...additionalInfo } as Tooth) : t
				);

				return this.additionalInfoRules.hasAdditionalInfoButtonSaved(overlaid, this.teethIds);
			}),
			distinctUntilChanged()
		);

		this.isReadOnly$ = modalData.isReadOnly$;
		this.showOfflineBanner$ = this.implantsViewService.showOfflineBanner$(this.isReadOnly$);

		this.showCrownRequiredInfoMessage$ = combineLatest([
			this.crownFormData$,
			this.additionalInfoData$,
			this.modalData.shouldValidateForSend$
		]).pipe(
			map(([crownForm, additionalInfo, shouldValidateForSend]) => {
				if (!shouldValidateForSend) {
					return false;
				}

				const crownSectionTooth = getNewTooth({
					MaterialID: crownForm.material,
					SpecificationId: crownForm.specification,
					ShadeBody: crownForm.shadeBody,
					CustomLabMaterialGroup: this.customLabMaterialGroup,
					...additionalInfo
				});

				return this.validateRxService.isCrownSectionInvalidForSend(crownSectionTooth);
			})
		);

		this.showCrownRequiredInfoBadge$ = combineLatest([this.crownSectionExpanded$, this.showCrownRequiredInfoMessage$]).pipe(
			map(([expanded, showMessage]) => !expanded && showMessage)
		);
	}

	get indicationId(): IntuitiveIndication {
		return this.teeth[0]?.indicationId ?? this.indicationMapper.getDefaultIndication();
	}

	get customLabMaterialGroup(): string | undefined {
		return this.teeth[0]?.customLabMaterialGroup;
	}

	get shouldShowCrownSection(): boolean {
		return this.modalData.shouldShowCrownSection;
	}

	get isBridge(): boolean {
		return this.modalData.isBridge;
	}

	ngOnInit(): void {
		this.initLegacyImplantsForm();

		if (!this.implantsViewService.shouldShowNewImplantsView()) {
			return;
		}

		/**
		 * Keep the latest matching implants-view event; the opener persists only that when the user chooses Done
		 * (see {@link RxIntuitiveTeethDiagramFacade} handling of {@link RxIntuitiveImplantsViewModalOutcome}).
		 */
		this.eventPipelineService
			.fromEvent<ImplantsViewDataChanged>(EventName.implantsViewDataChanged)
			.pipe(
				takeUntil(this.componentAlive$),
				filter(event => this.doesEventBelongToCurrentSession(event))
			)
			.subscribe(event => {
				this.latestImplantsViewEvent = event;
			});
	}

	onCrownFormChanged(crownForm: CrownForm): void {
		this.crownFormData$.next(crownForm);
	}

	toggleCrownSection(): void {
		this.crownSectionExpanded$.next(!this.crownSectionExpanded$.value);
	}

	onCrownSelectionContext(ctx: CrownSelectionContext): void {
		const prevMaterialOption = this.crownContext?.materialOption;

		this.crownContext = ctx;

		if (ctx.materialOption && prevMaterialOption !== ctx.materialOption) {
			this.clearDisabledAdditionalInfoFields(ctx.materialOption);
		}
	}

	onAdditionalInfoClick(): void {
		const crownContext = this.crownContext;

		if (!crownContext) {
			return;
		}

		combineLatest([this.isReadOnly$, this.modalData.shouldValidateForSend$])
			.pipe(
				take(1),
				switchMap(([isReadOnly, shouldValidateForSend]) =>
					this.additionalInfoModalService
						.open(isReadOnly, crownContext, this.additionalInfoData$.value, shouldValidateForSend)
						.pipe(take(1))
				),
				takeUntil(this.componentAlive$)
			)
			.subscribe({
				next: result => {
					if (!result) {
						return;
					}

					this.bufferAdditionalInfoResult(result);
				},
				error: err => {
					this.logger.error('Failed to open additional info modal', {
						module: 'RxIntuitiveImplantsViewModalComponent',
						extendedParameters: { error: String(err) }
					});
				}
			});
	}

	onRestorationTypeChanged(ids: RestorationTypeIds): void {
		this.restorationTypeData$.next(ids);
	}

	onLegacyFormValueChange(value: IntuitiveImplantsFormValue): void {
		const previousValue = this.legacyImplantsFormValue;
		let nextValue: IntuitiveImplantsFormValue = { ...value };

		const scanBodyChanged = previousValue.scanBodyManufacturer !== value.scanBodyManufacturer;
		const implantMfgChanged = previousValue.implantManufacturer !== value.implantManufacturer;

		if (scanBodyChanged) {
			this.implantManufacturerOptions = this.implantService.filterImplantManufacturers(value.scanBodyManufacturer);

			const implantMfg = value.implantManufacturer;

			if (this.implantManufacturerOptions?.includes(implantMfg as IdName)) {
				this.implantTypeScanBodyOptions = this.implantService.filterImplants(value.scanBodyManufacturer, implantMfg);

				if (!this.implantTypeScanBodyOptions?.includes(nextValue.implantTypeScanBody as Implant)) {
					nextValue = { ...nextValue, implantTypeScanBody: null };
				}
			} else {
				nextValue = { ...nextValue, implantManufacturer: null, implantTypeScanBody: null };
				this.implantTypeScanBodyOptions = [];
			}
		} else if (implantMfgChanged) {
			this.implantTypeScanBodyOptions = this.implantService.filterImplants(
				value.scanBodyManufacturer as IdName,
				value.implantManufacturer as IdName
			);

			if (!this.implantTypeScanBodyOptions?.includes(nextValue.implantTypeScanBody as Implant)) {
				nextValue = { ...nextValue, implantTypeScanBody: null };
			}
		}

		this.legacyImplantsFormValue = nextValue;
		this.changeDetectorRef.markForCheck();
	}

	handleDismiss(): void {
		this.dialogRef.close({ action: TOOTH_MODAL_ACTION.Cancel, payload: { toothPartialUpdate: null } });
	}

	handleSaveAndClose(): void {
		const crownAndRestorationPayload = {
			MaterialID: this.crownFormData$.value.material,
			ShadeSystemId: this.crownFormData$.value.shadeSystem,
			ShadeBody: this.crownFormData$.value.shadeBody,
			SpecificationId: this.crownFormData$.value.specification,
			...this.restorationTypeData$.value,
			...this.additionalInfoData$.value
		};

		if (this.implantsViewService.shouldShowNewImplantsView()) {
			const parsedImplantsViewData = this.implantsViewService.parseImplantsViewDataChangedEvent(this.latestImplantsViewEvent);

			this.dialogRef.close({
				action: TOOTH_MODAL_ACTION.Save,
				payload: {
					toothPartialUpdate: {
						ImplantsViewData: parsedImplantsViewData?.implantsViewData ?? null,
						...crownAndRestorationPayload
					},
					pendingAdditionalInfoClears: this.pendingAdditionalInfoClears.map(entry => ({
						toothIds: [...entry.toothIds],
						clearedFields: [...entry.clearedFields]
					}))
				}
			});

			return;
		}

		this.dialogRef.close({
			action: TOOTH_MODAL_ACTION.Save,
			payload: {
				toothPartialUpdate: {
					ImplantTypeID: this.legacyImplantsFormValue.implantTypeScanBody?.Id ?? 0,
					ImplantsViewData: null,
					...crownAndRestorationPayload
				},
				pendingAdditionalInfoClears: this.pendingAdditionalInfoClears.map(entry => ({
					toothIds: [...entry.toothIds],
					clearedFields: [...entry.clearedFields]
				}))
			}
		});
	}

	handleDelete(): void {
		this.dialogRef.close({ action: TOOTH_MODAL_ACTION.Delete, payload: { toothPartialUpdate: null } });
	}

	private initLegacyImplantsForm(): void {
		const initialId = this.teeth[0]?.implantTypeId;

		if (initialId) {
			const { scanBodyManufacturer, implantManufacturer, implantTypeScanBody } =
				this.implantService.getSelectedValuesByImplantId(initialId);

			this.implantManufacturerOptions = this.implantService.filterImplantManufacturers(scanBodyManufacturer);
			this.implantTypeScanBodyOptions = this.implantService.filterImplants(scanBodyManufacturer, implantManufacturer);
			this.legacyImplantsFormValue = {
				scanBodyManufacturer: scanBodyManufacturer ?? null,
				implantManufacturer: implantManufacturer ?? null,
				implantTypeScanBody: implantTypeScanBody ?? null
			};
		} else {
			this.implantManufacturerOptions = [];
			this.implantTypeScanBodyOptions = [];
			this.legacyImplantsFormValue = EMPTY_INTUITIVE_IMPLANTS_FORM_VALUE;
		}

		this.changeDetectorRef.markForCheck();
	}

	private bufferAdditionalInfoResult(result: AdditionalInfoResult): void {
		this.additionalInfoData$.next({ ...this.additionalInfoData$.value, ...result.values });

		if (result.clearedFields.length > 0) {
			this.pendingAdditionalInfoClears.push({ toothIds: [...this.teethIds], clearedFields: [...result.clearedFields] });
		}
	}

	private clearDisabledAdditionalInfoFields(materialOption: MaterialOption): void {
		const clearPayload = getDisabledFieldsClearPayload(materialOption);

		if (!clearPayload) {
			return;
		}

		this.additionalInfoData$.next({ ...this.additionalInfoData$.value, ...clearPayload });
	}

	private doesEventBelongToCurrentSession(event: ImplantsViewDataChanged): boolean {
		const toothNumber = event?.data?.toothNumber;
		const eventToothId = this.implantsViewService.parseImplantsViewEventToothId(toothNumber);

		return eventToothId != null && this.teethIds.includes(eventToothId);
	}

	private buildCrownForm(teeth: readonly ToothForImplantsViewModal[]): CrownForm {
		const firstTooth = teeth[0];

		return {
			teethIds: teeth.map(t => t.toothId),
			material: firstTooth?.material,
			shadeSystem: firstTooth?.shadeSystem,
			shadeBody: firstTooth?.shadeBody,
			specification: firstTooth?.specification
		};
	}
}
